import PropTypes from 'prop-types';
import React from 'react';
import FlexBox from 'flexbox-react';
import RouterLink from 'components/RouterLink';
import { ThemeProvider } from 'react-css-themr';
import Card from 'components/Card';
import PhoneLink from 'components/PhoneLink';
import DetailsAddress from 'components/DetailsAddress';
import { ResourceDetailsCardElement } from 'components/ResourceCardElements';
import InstallerPrioritizationSliders from 'components/InstallerPrioritizationSliders';
import MeasurePrioritizationSliders from 'components/MeasurePrioritizationSliders';
import theme from 'styles/resourceDetailsCard.scss';
import sectionTheme from 'styles/detailsSection.scss';
import detailsCardElement from 'styles/detailsCardElement.scss';
import StoreType from 'types/store';
import PricePatchType from 'types/pricePatch';

const detailsTheme = { RTCard: theme, ResourceDetailsCE: detailsCardElement };

const DetailsListingSection = (props) => {
  const { title, theme: t, items } = props;
  const sectionListItems = items.map(item =>
    <FlexBox className={t.sectionListItem} key={item.name} flexWrap="wrap" >
      <ResourceDetailsCardElement title="name" value={item.name} />
      <ResourceDetailsCardElement title="phone number" value={item.phone_number} />
      <ResourceDetailsCardElement title="email" value={item.email} />
      <ResourceDetailsCardElement title="position" value={item.position} />
      <FlexBox className={t.innerFlexSection}>
        <ResourceDetailsCardElement
          title="Daily Installation Updates"
          value={item.installation_updates ? 'Yes' : 'No'}
        />
        <ResourceDetailsCardElement
          title="Daily Assessment Updates"
          value={item.assessment_updates ? 'Yes' : 'No'}
        />
        <ResourceDetailsCardElement
          title="Email Opt-In"
          value={item.email_opt_in ? 'Yes' : 'No'}
        />
      </FlexBox>
    </FlexBox>,
  );
  return (<section className={t.section}>
    <span className={t.sectionTitle}>{title}</span>
    {sectionListItems}
  </section>);
};

DetailsListingSection.propTypes = {
  title: PropTypes.string,
  theme: PropTypes.shape({
    sectionListItem: PropTypes.string,
    section: PropTypes.string,
    sectionTitle: PropTypes.string,
  }),
  items: PropTypes.array,
};

const extractCandidateName = (data, fallback) =>
  ((data && typeof data === 'object' && data.name) ? data.name : fallback);

// Build installersBySurfaceType from stored installer weights for detail view
const getInstallersBySurfaceType = (weights, surfaceTypes) => {
  if (!weights || typeof weights !== 'object') return {};
  const result = {};
  surfaceTypes.forEach((st) => {
    const surfaceWeights = weights[st] || {};
    result[st] = Object.entries(surfaceWeights).map(([id, data]) => {
      const installerId = Number(id);
      const name = extractCandidateName(data, `Installer #${installerId}`);
      return { id: installerId, name, phase_count: 0 };
    });
  });
  return result;
};

// Build candidatesBySurfaceType from stored measure weights (composite key format)
const getMeasureCandidatesBySurfaceType = (weights, surfaceTypes) => {
  if (!weights || typeof weights !== 'object') return {};
  const result = {};
  surfaceTypes.forEach((st) => {
    const surfaceWeights = weights[st] || {};
    result[st] = Object.entries(surfaceWeights).map(([compositeId, data]) => {
      const name = extractCandidateName(data, compositeId);
      const assigneeType = (data && data.assignee_type) || (compositeId.startsWith('E_') ? 'Estimator' : 'Installer');
      return { composite_id: compositeId, name, phase_count: 0, assignee_type: assigneeType };
    });
  });
  return result;
};

const StoreDetailsCard = (props) => {
  const { store, canEdit, pricePatch, installerPrioritization,
    measurePrioritization } = props;
  const { vendor } = store;
  const storeContacts = (store.store_contacts || []);
  const ifiSurfaceTypes = store.ifi_surface_types || [];
  const weights = store.installer_prioritization_weights;
  const showWeightsSection = vendor === 'IFI';
  const installersBySurfaceType =
    (installerPrioritization && installerPrioritization.installersBySurfaceType) ||
    getInstallersBySurfaceType(weights, ifiSurfaceTypes);
  const measureWeights = store.measure_prioritization_weights;
  const showMeasureWeightsSection = vendor === 'IFI';
  const measureCandidatesBySurfaceType =
    (measurePrioritization && measurePrioritization.candidatesBySurfaceType) ||
    getMeasureCandidatesBySurfaceType(measureWeights, ifiSurfaceTypes);

  const EditAction = (<RouterLink className={theme.detailsLink} to={`/stores/${store.id}/edit`}>
    <span className={theme.editText}>Edit Store</span>
  </RouterLink>);
  const ccsSchedulableText = store.ccs_schedulable ? 'CCS Schedulable' : 'CCS Blocked';
  return (<ThemeProvider theme={detailsTheme}>
    <Card>
      <section className={sectionTheme.section}>
        <FlexBox justifyContent="space-between">
          <div>
            <div className={theme.mainTitle}>
              <span className={theme.boldWord}>#{store.number}</span><span>{` - ${store.name}`}</span>
              <span
                className={store.ccs_schedulable ? theme.activeText : theme.inactiveText}
              > ({ccsSchedulableText})</span>
            </div>
            <DetailsAddress
              className={theme.mainSubtitle}
              address={store.address}
              city={store.city}
              state={store.state}
              zipCode={store.zip_code}
            />
          </div>
          {canEdit ? EditAction : null}
        </FlexBox>
      </section>
      <hr className={theme.dividerLine} />
      <section className={sectionTheme.section}>
        <FlexBox flexWrap="wrap" className={theme.detailsElementContainer}>
          <ResourceDetailsCardElement title="vendor" value={store.vendor} />
          <ResourceDetailsCardElement title="phone number">
            <PhoneLink key={store.phone_number} number={store.phone_number} />
          </ResourceDetailsCardElement>
          <ResourceDetailsCardElement title="flooring fax" value={store.fax_number_flooring} />
          <ResourceDetailsCardElement title="installed sales fax" value={store.fax_number_installed_sales} />
          <ResourceDetailsCardElement title="receiving fax" value={store.fax_number_receiving} />
          <ResourceDetailsCardElement title={vendor === 'IFI' ? 'district' : 'workroom'} value={store.workroom_name} />
          <ResourceDetailsCardElement title="region" value={store.region_name} />
          <ResourceDetailsCardElement title="market" value={store.market_name} />
          <ResourceDetailsCardElement title="division" value={store.division_name} />
          <ResourceDetailsCardElement title="iso/ist" value={store.cpo_iso} />
          <ResourceDetailsCardElement title="price patch number" value={pricePatch.name} />
          <ResourceDetailsCardElement title="quickbooks id" value={store.quickbooks_ident} />
          <ResourceDetailsCardElement title="time zone" value={store.timezone} />
          <ResourceDetailsCardElement title="Disable IST Emails" value={store.disable_cpo_emails ? 'Yes' : 'No'} />
          <ResourceDetailsCardElement title="Delivery Method" value={store.delivery_method} />
          <ResourceDetailsCardElement title="Express Ship Days" value={store.express_ship_days} />
          <ResourceDetailsCardElement title="covered zip codes">
            <span className={theme.viewableZips}>
              {(store.covered_zip_codes || []).sort((a, b) => a - b).join(', ') || ' -- '}
            </span>
          </ResourceDetailsCardElement>
        </FlexBox>
      </section>
      <hr className={theme.dividerLine} />
      <DetailsListingSection title="Store Contacts" items={storeContacts} theme={sectionTheme} />
      {showMeasureWeightsSection && (
        <section className={sectionTheme.section}>
          <hr className={theme.dividerLine} />
          <span className={sectionTheme.sectionTitle}>Measure Prioritization Weight</span>
          <MeasurePrioritizationSliders
            weights={measureWeights}
            candidatesBySurfaceType={measureCandidatesBySurfaceType}
            surfaceTypes={ifiSurfaceTypes}
            onChange={() => {}}
            disabled
          />
        </section>
      )}
      {showWeightsSection && (
        <section className={sectionTheme.section}>
          <hr className={theme.dividerLine} />
          <span className={sectionTheme.sectionTitle}>Installer Prioritization Weights</span>
          <InstallerPrioritizationSliders
            weights={weights}
            installersBySurfaceType={installersBySurfaceType}
            surfaceTypes={ifiSurfaceTypes}
            onChange={() => {}}
            disabled
          />
        </section>
      )}
    </Card>
  </ThemeProvider>);
};

StoreDetailsCard.propTypes = {
  store: StoreType.isRequired,
  canEdit: PropTypes.bool,
  pricePatch: PricePatchType,
  installerPrioritization: PropTypes.object,
  measurePrioritization: PropTypes.object,
};

export default StoreDetailsCard;



// WEBPACK FOOTER //
// ./client/app/screens/main/screens/stores/components/storeDetailsCard.jsx